import { useScrollAnimation } from '../hooks/useScrollAnimation';

interface RowData {
  criteria: string;
  nothing: { text: string; positive: boolean };
  chatbot: { text: string; positive: boolean };
  automat: { text: string; positive: boolean };
}

const rows: RowData[] = [
  {
    criteria: 'Tempo de resposta',
    nothing: { text: 'Horas (ou nunca)', positive: false },
    chatbot: { text: 'Segundos (robótico)', positive: false },
    automat: { text: 'Instantâneo (inteligente)', positive: true },
  },
  {
    criteria: 'Disponibilidade',
    nothing: { text: 'Horário comercial', positive: false },
    chatbot: { text: '24/7', positive: false },
    automat: { text: '24/7', positive: true },
  },
  {
    criteria: 'Entende seu produto',
    nothing: { text: 'Não', positive: false },
    chatbot: { text: 'Não', positive: false },
    automat: { text: 'Sim (treinado no catálogo)', positive: true },
  },
  {
    criteria: 'Custo mensal',
    nothing: { text: 'R$0 (mas perde vendas)', positive: false },
    chatbot: { text: 'R$200–500', positive: false },
    automat: { text: 'Fração do custo de 1 SDR', positive: true },
  },
  {
    criteria: 'Escala com volume',
    nothing: { text: 'Não', positive: false },
    chatbot: { text: 'Sim (irrita o cliente)', positive: false },
    automat: { text: 'Sim (com qualidade)', positive: true },
  },
  {
    criteria: 'Qualifica leads',
    nothing: { text: 'Não', positive: false },
    chatbot: { text: 'Não', positive: false },
    automat: { text: 'Sim (automaticamente)', positive: true },
  },
  {
    criteria: 'CRM integrado',
    nothing: { text: 'Não', positive: false },
    chatbot: { text: 'Geralmente não', positive: false },
    automat: { text: 'Nativo e completo', positive: true },
  },
];

function CellValue({ value }: { value: { text: string; positive: boolean } }) {
  return (
    <div className="flex items-center gap-2">
      {value.positive ? (
        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
          <i className="ri-check-line text-green-600 text-xs" />
        </div>
      ) : (
        <div className="w-5 h-5 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
          <i className="ri-close-line text-red-400 text-xs" />
        </div>
      )}
      <span className="text-sm leading-snug">{value.text}</span>
    </div>
  );
}

function MobileCard({
  title,
  highlight,
  iconBg,
  textColor,
  bgColor,
  borderColor,
  values,
  delay,
  visible,
}: {
  title: string;
  highlight?: string;
  iconBg: string;
  textColor: string;
  bgColor: string;
  borderColor: string;
  values: { criteria: string; value: { text: string; positive: boolean } }[];
  delay: number;
  visible: boolean;
}) {
  return (
    <div
      className={`rounded-2xl overflow-hidden border-2 ${borderColor} ${bgColor} transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
    >
      {/* Card header */}
      <div className={`${iconBg} px-5 py-4 flex items-center justify-between`}>
        <span className={`font-b2b font-bold text-lg ${textColor}`}>{title}</span>
        {highlight && (
          <span className="bg-coral text-white text-xs font-b2b font-bold px-3 py-1 rounded-full">
            {highlight}
          </span>
        )}
      </div>
      {/* Rows */}
      <div className="divide-y divide-gray-100/50">
        {values.map(({ criteria, value }) => (
          <div key={criteria} className="px-5 py-3.5">
            <p className="font-b2bbody text-xs text-gray-400 mb-1 uppercase tracking-wider font-medium">
              {criteria}
            </p>
            <div className={value.positive ? 'text-gray-900' : 'text-gray-400'}>
              <CellValue value={value} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Comparison() {
  const { ref, visible } = useScrollAnimation(0.08);

  return (
    <section id="comparison" className="bg-b2bgray py-24 lg:py-32 px-6">
      <div className="max-w-6xl mx-auto" ref={ref}>
        {/* Badge */}
        <div
          className={`flex justify-center mb-5 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="font-b2bbody text-xs font-semibold tracking-widest uppercase border border-coral/30 text-coral rounded-full px-4 py-1.5">
            Comparação
          </span>
        </div>

        {/* Title */}
        <div
          className={`text-center mb-16 transition-all duration-700 delay-75 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="font-b2b text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-navy leading-tight">
            Por que a Automat e não as{' '}
            <span className="text-coral">alternativas?</span>
          </h2>
        </div>

        {/* ── DESKTOP TABLE ── */}
        <div
          className={`hidden lg:block transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="rounded-2xl overflow-hidden border border-gray-200">
            {/* Table header */}
            <div className="grid grid-cols-4">
              {/* Criteria col */}
              <div className="bg-white px-6 py-5 border-b border-gray-200">
                <span className="font-b2b text-sm font-bold text-gray-400 uppercase tracking-wider">
                  Critério
                </span>
              </div>
              {/* Não fazer nada */}
              <div className="bg-gray-50 px-6 py-5 border-b border-l border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                    <i className="ri-close-circle-line text-gray-400 text-sm" />
                  </div>
                  <span className="font-b2b text-sm font-bold text-gray-500">Não fazer nada</span>
                </div>
              </div>
              {/* Chatbot genérico */}
              <div className="bg-gray-50 px-6 py-5 border-b border-l border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                    <i className="ri-robot-line text-gray-400 text-sm" />
                  </div>
                  <span className="font-b2b text-sm font-bold text-gray-500">Chatbot Genérico</span>
                </div>
              </div>
              {/* Automat — highlighted */}
              <div
                className="px-6 py-5 border-b border-l border-navy/20"
                style={{ background: 'linear-gradient(135deg, #0A1628, #162D5A)' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-coral flex items-center justify-center">
                      <i className="ri-robot-2-line text-white text-sm" />
                    </div>
                    <span className="font-b2b text-sm font-bold text-white">Automat</span>
                  </div>
                  <span className="bg-coral text-white text-[10px] font-b2b font-bold px-2.5 py-0.5 rounded-full">
                    RECOMENDADO
                  </span>
                </div>
              </div>
            </div>

            {/* Table rows */}
            {rows.map((row, i) => (
              <div
                key={row.criteria}
                className={`grid grid-cols-4 ${i < rows.length - 1 ? 'border-b border-gray-100' : ''}`}
              >
                {/* Criteria */}
                <div className="bg-white px-6 py-4 flex items-center">
                  <span className="font-b2bbody text-sm font-semibold text-gray-700">
                    {row.criteria}
                  </span>
                </div>

                {/* Nothing */}
                <div className="bg-gray-50 px-6 py-4 border-l border-gray-100 flex items-center text-gray-400">
                  <CellValue value={row.nothing} />
                </div>

                {/* Chatbot */}
                <div className="bg-gray-50 px-6 py-4 border-l border-gray-100 flex items-center text-gray-400">
                  <CellValue value={row.chatbot} />
                </div>

                {/* Automat */}
                <div
                  className="px-6 py-4 border-l border-navy/10 flex items-center"
                  style={{
                    background: i % 2 === 0
                      ? 'linear-gradient(135deg, rgba(10,22,40,0.97), rgba(22,45,90,0.97))'
                      : 'linear-gradient(135deg, rgba(10,22,40,0.92), rgba(17,36,72,0.92))',
                  }}
                >
                  <div className="text-green-400">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-green-400 text-xs" />
                      </div>
                      <span className="text-sm text-white font-medium leading-snug">
                        {row.automat.text}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── MOBILE CARDS ── */}
        <div className="lg:hidden space-y-4">
          <MobileCard
            title="Não fazer nada"
            iconBg="bg-gray-100"
            textColor="text-gray-600"
            bgColor="bg-white"
            borderColor="border-gray-200"
            values={rows.map((r) => ({ criteria: r.criteria, value: r.nothing }))}
            delay={0}
            visible={visible}
          />
          <MobileCard
            title="Chatbot Genérico"
            iconBg="bg-gray-100"
            textColor="text-gray-600"
            bgColor="bg-white"
            borderColor="border-gray-200"
            values={rows.map((r) => ({ criteria: r.criteria, value: r.chatbot }))}
            delay={100}
            visible={visible}
          />
          <MobileCard
            title="Automat"
            highlight="VENCEDOR"
            iconBg="bg-navy"
            textColor="text-white"
            bgColor="bg-white"
            borderColor="border-coral"
            values={rows.map((r) => ({ criteria: r.criteria, value: r.automat }))}
            delay={200}
            visible={visible}
          />
        </div>

        {/* Bottom note */}
        <div
          className={`mt-10 text-center transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: visible ? '500ms' : '0ms' }}
        >
          <p className="font-b2bbody text-gray-400 text-sm">
            * Valores estimados. O custo da Automat varia conforme o plano.
          </p>
        </div>
      </div>
    </section>
  );
}